import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { formatDateTime } from '@/lib/dates'

export type CreatedByInfo = {
  clerk_user_id: string | null
  agent: string | null
  display_name: string | null
} | null

/**
 * Small "i" icon next to a header that opens a tooltip showing when a
 * row was created and by whom. Used on customer + vehicle detail pages.
 *
 * Rendered states:
 *   - createdBy.display_name resolved   → "Created <time> by <name>"
 *   - createdBy.agent === 'eric' only   → "Created <time> by Eric"
 *   - createdBy null AND createdAt set  → "Created <time> (creator
 *                                          unknown — pre-tracking)"
 *   - createdAt also null               → returns null (no tooltip)
 *
 * Tooltip primitive (radix-ui) handles both hover (desktop) and tap
 * (mobile via focus) out of the box.
 */
export function CreatedByTooltip({
  createdAt,
  createdBy,
}: {
  createdAt: string | null | undefined
  createdBy: CreatedByInfo
}) {
  const { t } = useTranslation()
  if (!createdAt) return null

  const datetime = formatDateTime(createdAt)
  let label: string
  if (createdBy?.display_name) {
    label = t('common:created_by.by', {
      datetime,
      name: createdBy.display_name,
    })
  } else if (createdBy?.agent === 'eric') {
    label = t('common:created_by.by_eric_no_assistant', { datetime })
  } else if (createdBy?.clerk_user_id) {
    label = t('common:created_by.anonymous', { datetime })
  } else {
    label = t('common:created_by.pre_tracking', { datetime })
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t('common:created_by.icon_aria')}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1"
        >
          <Info className="h-4 w-4" aria-hidden="true" />
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}
